/**
 * @fileoverview MainLayout — Header + Outlet + Footer sarmalayıcısı.
 * ScrollToTop: Route değişiminde sayfanın tepesine otomatik kayar.
 * GSAP: ScrollTrigger yalnızca gerekli rotalarda dinamik import (contact vb. GSAP indirmez); refresh + üç rAF.
 * CMS: Sayfa-içi namespace fetch’leri rotaya göre (`SiteCmsSync`); siteLayout + diller her sayfada.
 */

import { Outlet, useLocation } from 'react-router-dom'

function routeCmsFlags(pathname) {
  const p = pathname || '/'
  return {
    home: p === '/',
    about: p === '/about' || p.startsWith('/about/'),
    portfolio: p === '/portfolio' || p.startsWith('/portfolio/'),
    contact: p === '/contact' || p.startsWith('/contact/'),
  }
}

/** ScrollTrigger / SectionHeading kullanan sayfalar — diğerlerinde refresh zorunlu reflow üretir, faydası yok. */
function routeUsesScrollTrigger(pathname) {
  const f = routeCmsFlags(pathname)
  return f.home || f.about || f.portfolio
}
import { useEffect } from 'react'
import Header from '../components/layout/Header'
import Footer from '../components/layout/Footer'
import { useHomeCmsSync } from '../features/home/useHomeCmsSync'
import { useAboutCmsSync } from '../features/about/useAboutCmsSync'
import { usePortfolioCmsSync } from '../features/portfolio/usePortfolioCmsSync'
import { useContactCmsSync } from '../features/contact/useContactCmsSync'
import { useSiteLayoutCmsSync } from '../features/siteLayout/useSiteLayoutCmsSync'
import { useSiteLanguagesBootstrap } from '../features/siteLocale/useSiteLanguagesBootstrap'

/** MainLayout — contact vb. rotalarda GSAP paketini hiç çekmemek için tek paylaşımlı yükleme */
let scrollTriggerPluginPromise

function loadScrollTriggerPlugin() {
  if (!scrollTriggerPluginPromise) {
    scrollTriggerPluginPromise = Promise.all([import('gsap'), import('gsap/ScrollTrigger')]).then(
      ([gsapMod, stMod]) => {
        const gsap = gsapMod.default
        const { ScrollTrigger } = stMod
        gsap.registerPlugin(ScrollTrigger)
        return ScrollTrigger
      }
    )
  }
  return scrollTriggerPluginPromise
}

function SiteCmsSync() {
  const { pathname } = useLocation()
  const cms = routeCmsFlags(pathname)

  useSiteLanguagesBootstrap()
  useSiteLayoutCmsSync()
  useHomeCmsSync({ enabled: cms.home })
  useAboutCmsSync({ enabled: cms.about })
  usePortfolioCmsSync({ enabled: cms.portfolio })
  useContactCmsSync({ enabled: cms.contact })
  return null
}

/** Route değişiminde en üste kaydırmak ve ScrollTrigger refresh için iç bileşen */
function ScrollToTop() {
  const { pathname } = useLocation()

  useEffect(() => {
    let cancelled = false
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })

    if (!routeUsesScrollTrigger(pathname)) {
      return () => {
        cancelled = true
      }
    }

    let raf1 = 0
    let raf2 = 0
    let raf3 = 0

    void loadScrollTriggerPlugin().then((ScrollTrigger) => {
      if (cancelled) return
      // Lazy sayfa (Suspense) commit + paint sonrası ölçüm: üç rAF, tek refresh
      raf1 = requestAnimationFrame(() => {
        if (cancelled) return
        raf2 = requestAnimationFrame(() => {
          if (cancelled) return
          raf3 = requestAnimationFrame(() => {
            if (!cancelled) ScrollTrigger.refresh()
          })
        })
      })
    })

    return () => {
      cancelled = true
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
      cancelAnimationFrame(raf3)
    }
  }, [pathname])

  return null
}

export default function MainLayout() {
  return (
    <div className="flex flex-col min-h-screen bg-[#1A1A1A] text-white">
      <SiteCmsSync />
      <ScrollToTop />

      {/* Sticky Header */}
      <Header />

      {/* Sayfa içeriği — Header yüksekliği için üst padding */}
      <main
        id="main-content"
        className="flex-1"
        tabIndex={-1}   /* Skip-to-content erişilebilirliği için */
      >
        <Outlet />
      </main>

      {/* Footer */}
      <Footer />
    </div>
  )
}
